import React, { useContext, useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Icon from "@material-ui/core/Icon";
import Store from "@material-ui/icons/Store";
import DateRange from "@material-ui/icons/DateRange";
import Update from "@material-ui/icons/Update";
import Accessibility from "@material-ui/icons/Accessibility";
import GridItem from "../../Components/Grid/GridItem";
import GridContainer from "../../Components/Grid/GridContainer.js";
import Card from "../../Components/Card/Card.js";
import CardHeader from "../../Components/Card/CardHeader.js";
import CardIcon from "../../Components/Card/CardIcon.js";
import CardFooter from "../../Components/Card/CardFooter.js";
import styles from "../../assets/jss/material-dashboard-react/views/dashboardStyle.js";
import { PageStatus } from "../../enums";
import { PartnersAPI, SurveysAPI } from "../../API";
import { connect } from "react-redux";
import { Alert, Button, Container, Spinner } from "react-bootstrap";
import { Show } from "../../Layout";
import { exportToExcel } from "../../Utils/ExportToExcel";
import Select from 'react-select';
import LanguageContext from "../../Layout/LanguageContext";
import { pageDict } from "../../Languages/PageTranslations";
import Swal from 'sweetalert2';
const useStyles = makeStyles(styles);

function AdminDashboard({ ...rest }) {
    const classes = useStyles();
    const lang = useContext(LanguageContext) || 'en';
    const t = (key) => (pageDict[lang] && pageDict[lang][key]) || pageDict['en'][key] || key;
    const [data, setData] = useState(null);
    const [status, setStatus] = useState(PageStatus.None);
    const [error, setError] = useState('');
    const [isExporting, setIsExporting] = useState(false);
    const [partners, setPartners] = useState([]);
    const [surveys, setSurveys] = useState([]);
    const [selectPartners, setSelectPartners] = useState(null);
    const [selectSurvey, setSelectSurvey] = useState(null);


    useEffect(() => {
        let isMounted = true;
        const fetchData = async () => {
            if (isMounted) setStatus(PageStatus.Loading);
            SurveysAPI.adminRespondentDashboardWeb()
                .then((response) => {
                    if (isMounted && response) {
                        setData(response);
                        setStatus(PageStatus.Loaded);
                    }
                })
                .catch((error) => {
                    if (isMounted) {
                        setStatus(PageStatus.Error);
                        setError(error.message);
                    }
                });
        };

        fetchData();
        fetchPartners();
        
        return () => {
            isMounted = false;
        };
    }, [rest.selectedCountry]);


    const fetchPartners = () => {
        Promise.resolve()
            .then(() => {
                return PartnersAPI.getPartners(10000);
            })
            .then((survey) => {
                const options = survey.map((item) => ({
                    label: item.name,
                    value: item.id,
                }));
                setPartners(options);
            })
            .catch((error) => {
                setError(error);
            });
    }


    const fetchPartnersSurveys = (id) => {
        Promise.resolve()
            .then(() => {
                return PartnersAPI.getAllPartnerSurveys(id);
            })
            .then((survey) => {
                const options = survey.map((item) => ({
                    label: item.survey.name,
                    value: item.survey.id,
                }));
                setSurveys(options);
            })
            .catch((error) => {
                setError(error);
            });
    }


    const calculateTime = (createdAt, updatedAt) => {
        const created = new Date(createdAt);
        const updated = new Date(updatedAt);
        const timeDifferenceInMilliseconds = updated - created;
        return timeDifferenceInMilliseconds / (1000 * 60);
    }
    const fetchPartnerUsers = async () => {
        if (!selectPartners || !selectSurvey) {
            Swal.fire({ icon: 'warning', title: 'Selection Required', text: 'Please select both a partner and a survey before downloading.' });
            return;
        }
        setIsExporting(true);
        try {
            const response = await SurveysAPI.partnerSurveyUsers(selectPartners.value, selectSurvey.value);
            if (!response || response.length === 0) {
                Swal.fire({ icon: 'info', title: 'No Data', text: 'No data found for the selected partner and survey.' });
                setIsExporting(false);
                return;
            }
            const transformedData = response.map(item => ({
                "Survey ID of the Project": item.survey_id,
                "Country": item.country,
                "India Polls respondent ID": item.id,
                "Vendor Respondent ID": item.rid,
                "Status": item.status,
                "Start IP": item.ip,
                "End IP": item.ip,
                "Start Time": item.createdAt,
                "End Time": item.updatedAt,
                "LOI (Minutes)": calculateTime(item.createdAt, item.updatedAt),
                "Survey Name": item.surveyName,
                "Partner Name": item.partnerName
            }));
            exportToExcel(transformedData, 'PartnerUsers');
        } catch (error) {
            Swal.fire({ icon: 'error', title: 'Download Failed', text: error.message || 'Failed to download report. Please try again.' });
        } finally {
            setIsExporting(false);
        }
    };

    const handlePartnersChange = (selectedPartnerOption) => {
        setSelectPartners(selectedPartnerOption);
        setSurveys([]);
        setSelectSurvey(null);
        fetchPartnersSurveys(selectedPartnerOption.value)
    };

    const handlePartnersSurveyChange = (selectedSurveyOption) => {
        setSelectSurvey(selectedSurveyOption);
    };

    const customStyles = {
        option: (provided, state) => ({
            ...provided,
            color: state.isSelected ? 'white' : 'black',
        }),
    };

    return (
        <div>
            {/*<ReactStars count={5} value={5} size={24} activeColor="#ffd700"/>*/}
            <GridContainer>

                <Show when={status === PageStatus.Loading}>
                    <Container className="d-flex justify-content-center mt-5">
                        <Spinner animation="border" variant="primary" />
                    </Container>
                </Show>

                <Alert
                    variant="danger"
                    show={status === PageStatus.Error}
                >
                    {error}
                </Alert>


                <Show when={status === PageStatus.Loaded && !!data}>
                    <GridItem xs={12} sm={6} md={3}>
                        <Card>
                            <CardHeader color="info" stats icon>
                                <CardIcon color="warning">
                                    <Icon>content_copy</Icon>
                                </CardIcon>
                                <p className={classes.cardCategory}>{data?.totalSurveys.name || ''}</p>
                                <h3 className={classes.cardTitle}>
                                    {data ? data.totalSurveys.points : 0}<small></small>
                                </h3>
                            </CardHeader>
                            <CardFooter stats>
                                <div className={classes.stats}>
                                    <Update />
                                    {t("Just Updated")}
                                </div>
                            </CardFooter>
                        </Card>
                    </GridItem>
                    <GridItem xs={12} sm={6} md={3}>
                        <Card>
                            <CardHeader color="warning" stats icon>
                                <CardIcon color="success">
                                    <Store />
                                </CardIcon>
                                <p className={classes.cardCategory}>{data?.completeSurveys.name || ''}</p>
                                <h3 className={classes.cardTitle}>
                                    {data ? data.completeSurveys.points : 0}<small></small>
                                </h3>
                            </CardHeader>
                            <CardFooter stats>
                                <div className={classes.stats}>
                                    <Update />
                                    {t("Just Updated")}
                                </div>
                            </CardFooter>
                        </Card>
                    </GridItem>
                    <GridItem xs={12} sm={6} md={3}>
                        <Card>
                            <CardHeader color="success" stats icon>
                                <CardIcon color="danger">
                                    <Icon>info_outline</Icon>
                                </CardIcon>
                                <p className={classes.cardCategory}>{data?.incompleteSurveys.name || ''}</p>
                                <h3 className={classes.cardTitle}>
                                    {data ? data.incompleteSurveys.points : 0}<small></small>
                                </h3>
                            </CardHeader>
                            <CardFooter stats>
                                <div className={classes.stats}>
                                    <DateRange />
                                </div>
                            </CardFooter>
                        </Card>
                    </GridItem>
                    <GridItem xs={12} sm={6} md={3}>
                        <Card>
                            <CardHeader color="danger" stats icon>
                                <CardIcon color="info">
                                    <Accessibility />
                                </CardIcon>
                                <p className={classes.cardCategory}>{data?.notStartedSurveys.name || ''}</p>
                                <h3 className={classes.cardTitle}>
                                    {data ? data.notStartedSurveys.points : 0}<small></small>
                                </h3>
                            </CardHeader>
                            <CardFooter stats>
                                <div className={classes.stats}>
                                    <Update />
                                    {t("Just Updated")}
                                </div>
                            </CardFooter>
                        </Card>
                    </GridItem>
                    <GridItem xs={12} sm={6} md={3}>
                        <Card>
                            <CardHeader color="info" stats icon>
                                <CardIcon color="warning">
                                    <Icon>content_copy</Icon>
                                </CardIcon>
                                <p className={classes.cardCategory}>{data?.overallAttemptedPercentage.name || ''}</p>
                                <h3 className={classes.cardTitle}>
                                    {data ? data.overallAttemptedPercentage.points : 0}<small>%</small>
                                </h3>
                            </CardHeader>
                            <CardFooter stats>
                                <div className={classes.stats}>
                                    <Update />
                                    {t("Just Updated")}
                                </div>
                            </CardFooter>
                        </Card>
                    </GridItem>
                    <GridItem xs={12} sm={6} md={3}>
                        <Card>
                            <CardHeader color="info" stats icon>
                                <CardIcon color="success">
                                    <Store />
                                </CardIcon>
                                <p className={classes.cardCategory}>{data ? data.totalLeft.name : 0}</p>
                                <h3 className={classes.cardTitle}>
                                    {data ? data.totalLeft.points : 0}<small></small>
                                </h3>
                            </CardHeader>
                            <CardFooter stats>
                                <div className={classes.stats}>
                                    <Update />
                                    {t("Just Updated")}
                                </div>
                            </CardFooter>
                        </Card>
                    </GridItem>
                    <GridItem xs={12} sm={6} md={3}>
                        <Card>
                            <CardHeader color="info" stats icon>
                                <CardIcon color="danger">
                                    <Icon>info_outline</Icon>
                                </CardIcon>
                                <p className={classes.cardCategory}>{data?.totalReferralsApproved.name || ''}</p>
                                <h3 className={classes.cardTitle}>
                                    {data ? data.totalReferralsApproved.points : 0}<small></small>
                                </h3>
                            </CardHeader>
                            <CardFooter stats>
                                <div className={classes.stats}>
                                    <Update />
                                    {t("Just Updated")}
                                </div>
                            </CardFooter>
                        </Card>
                    </GridItem>
                    <GridItem xs={12} sm={6} md={3}>
                        <Card>
                            <CardHeader color="info" stats icon>
                                <CardIcon color="info">
                                    <Accessibility />
                                </CardIcon>
                                <p className={classes.cardCategory}>{data?.totalReferralsPoints.name || ''}</p>
                                <h3 className={classes.cardTitle}>
                                    {data ? data.totalReferralsPoints.points : 0}<small></small>
                                </h3>
                            </CardHeader>
                            <CardFooter stats>
                                <div className={classes.stats}>
                                    <Update />
                                    {t("Just Updated")}
                                </div>
                            </CardFooter>
                        </Card>
                    </GridItem>
                    <GridItem xs={12} sm={6} md={3}>
                        <Card>
                            <CardHeader color="info" stats icon>
                                <CardIcon color="warning">
                                    <Icon>content_copy</Icon>
                                </CardIcon>
                                <p className={classes.cardCategory}>{data?.totalReferralsStatistics.name || ''}</p>
                                <h3 className={classes.cardTitle}>
                                    {data ? data.totalReferralsStatistics.points : 0}<small></small>
                                </h3>
                            </CardHeader>
                            <CardFooter stats>
                                <div className={classes.stats}>
                                    <Update />
                                    {t("Just Updated")}
                                </div>
                            </CardFooter>
                        </Card>
                    </GridItem>
                    <GridItem xs={12} sm={6} md={3}>
                        <Card>
                            <CardHeader color="info" stats icon>
                                <CardIcon color="success">
                                    <Store />
                                </CardIcon>
                                <p className={classes.cardCategory}>{data?.totalRewardPoints.name || ''}</p>
                                <h3 className={classes.cardTitle}>
                                    {data ? data.totalRewardPoints.points : 0}<small></small>
                                </h3>
                            </CardHeader>
                            <CardFooter stats>
                                <div className={classes.stats}>
                                    <Update />
                                    {t("Just Updated")}
                                </div>
                            </CardFooter>
                        </Card>
                    </GridItem>
                    <GridItem xs={12} sm={6} md={6}>
                        <Card>
                            <CardHeader color="info" stats icon>
                                <CardIcon color="danger">
                                    <Icon>info_outline</Icon>
                                </CardIcon>
                                <Select
                                    name='partners'
                                    id='partners'
                                    className="ql-color-red"
                                    placeholder={t("Select Partner")}
                                    onChange={handlePartnersChange}
                                    value={selectPartners}
                                    options={partners}
                                    styles={customStyles}
                                />
                                <Select
                                    name='partners'
                                    id='partners'
                                    className="ml-100 mt-1 align-content-end"
                                    placeholder={t("Select Survey")}
                                    onChange={handlePartnersSurveyChange}
                                    value={selectSurvey}
                                    options={surveys}
                                    styles={customStyles}
                                />
                                <div>{t("Partners")}</div>
                                <p className={classes.cardCategory}>{t("Reports")}</p>
                                <Button
                                    onClick={() => fetchPartnerUsers()}
                                    disabled={isExporting === true || !selectPartners || !selectSurvey}
                                >
                                    {t("Download")}
                                </Button>
                                <Show when={isExporting === true}>
                                    <Spinner size="sm" animation="border" variant="primary" />
                                </Show>
                            </CardHeader>
                            <CardFooter stats>
                                <div className={classes.stats}>
                                    <Update />
                                    {t("Just Updated")}
                                </div>
                            </CardFooter>
                        </Card>
                    </GridItem>
                </Show>
            </GridContainer>
        </div>
    );
}

function mapStateToProps(state) {
    return {
        userId: state.adminUser.adminUser.userId,
        role: state.adminUser.adminUser.role,
        selectedCountry: state.adminUser.adminUser.selectedCountry,
    };
}

export default connect(mapStateToProps)(AdminDashboard);
